import PptxGenJS from "pptxgenjs";
import Swal from "sweetalert2";

/* ============================================================
 * Image Loader + Cache
 * ============================================================ */

const yieldToMain = () => {
  return new Promise((resolve) => {
    requestAnimationFrame(resolve);
  });
};

const imageCache = new Map();

const MAX_CONCURRENT_DOWNLOADS = 4;
const MAX_IMAGE_DIMENSION = 1800;
const JPEG_QUALITY = 0.82;

class PromisePool {
  constructor(limit) {
    this.limit = limit;
    this.running = 0;
    this.queue = [];
  }

  async add(task) {
    if (this.running >= this.limit) {
      await new Promise((resolve) => this.queue.push(resolve));
    }

    this.running++;

    try {
      return await task();
    } finally {
      this.running--;

      if (this.queue.length) {
        this.queue.shift()();
      }
    }
  }
}

const pool = new PromisePool(MAX_CONCURRENT_DOWNLOADS);

let failedImageUrls = new Set();

const resizeImageBlob = async (blob) => {
  const bitmap = await createImageBitmap(blob);
  const sourceWidth = bitmap.width;
  const sourceHeight = bitmap.height;
  const scale = Math.min(
    1,
    MAX_IMAGE_DIMENSION / Math.max(sourceWidth, sourceHeight),
  );
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d", { alpha: false });
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const mimeType = "image/jpeg";
  const data = canvas.toDataURL(mimeType, JPEG_QUALITY);

  canvas.width = 0;
  canvas.height = 0;

  return { data, width, height };
};

async function loadImage(url, skipResize = false) {
  if (!url) return null;

  if (imageCache.has(url)) {
    return imageCache.get(url);
  }

  const promise = pool.add(async () => {
    const tryFetch = async (fetchUrl, options) => {
      const response = await fetch(fetchUrl, options);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error(
          "Empty response body (likely an opaque/CORS-blocked response)",
        );
      }
      return blob;
    };

    try {
      let blob;

      try {
        blob = await tryFetch(url, { mode: "cors" });
      } catch (firstAttemptError) {
        console.warn(
          `[PPT export] First fetch failed for ${url}, retrying without cache:`,
          firstAttemptError,
        );
        const bustedUrl = `${url}${url.includes("?") ? "&" : "?"}_cb=${Date.now()}`;
        blob = await tryFetch(bustedUrl, { mode: "cors", cache: "no-store" });
      }

      if (skipResize) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              data: reader.result,
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }

      return await resizeImageBlob(blob);
    } catch (e) {
      console.warn(`[PPT export] Skipped image (failed to load): ${url}`, e);
      failedImageUrls.add(url);

      return null;
    }
  });

  imageCache.set(url, promise);

  return promise;
}

const getImageUrlsForLayout = (recceData, selectedLayoutType) => {
  const urls = new Set();

  recceData.forEach((recce) => {
    const job = recce.job || {};
    const installationJob = recce.installationJob;

    if (job.imageOfRecceSpace) urls.add(job.imageOfRecceSpace);

    if (selectedLayoutType === "creative-ppt" && job.clientSideAdaptationImage)
      urls.add(job.clientSideAdaptationImage);

    if (
      (selectedLayoutType === "creative-ppt" ||
        selectedLayoutType === "completion-ppt") &&
      job.adaptationImage
    )
      urls.add(job.adaptationImage);

    if (
      selectedLayoutType === "completion-ppt" &&
      installationJob?.imagesOfInstallation?.frontPhoto
    ) {
      urls.add(installationJob?.imagesOfInstallation?.frontPhoto);
    }
  });

  return [...urls];
};

async function preloadImages(recceData, selectedLayoutType, onProgress) {
  const images = getImageUrlsForLayout(recceData, selectedLayoutType);
  const total = images.length;

  let completed = 0;
  const concurrency = 5;

  async function downloadImage(imageUrl) {
    try {
      await loadImage(imageUrl);
    } catch (error) {
      console.warn("Image download failed:", imageUrl, error);
    }

    completed++;

    if (completed % 5 === 0 || completed === total) {
      onProgress?.({
        phase: "Downloading Images",
        current: completed,
        total,
      });

      await yieldToMain();
    }
  }

  for (let index = 0; index < total; index += concurrency) {
    const batch = images.slice(index, index + concurrency);

    await Promise.all(batch.map((imageUrl) => downloadImage(imageUrl)));
  }
}

function addSafeText(slide, panelX, panelW, text, y, bold = false) {
  const charsPerLine = 35;

  const lines = Math.ceil(text.length / charsPerLine);

  const lineHeight = 0.18;

  const dynamicHeight = Math.max(0.3, lines * lineHeight);

  slide.addText(text, {
    x: panelX + 0.08,
    y,
    w: panelW - 0.2,
    h: dynamicHeight,

    fontSize: 11,

    color: "374151",

    bold,

    wrap: true,
  });

  return dynamicHeight;
}

//  PASTE renderImageContain HERE
const renderImageContain = async (slide, url, container) => {
  const image = await loadImage(url);
  if (!image) return;

  const imgRatio = image.width / image.height;
  const containerRatio = container.w / container.h;

  let w;
  let h;

  if (imgRatio > containerRatio) {
    w = container.w;
    h = w / imgRatio;
  } else {
    h = container.h;
    w = h * imgRatio;
  }

  slide.addImage({
    data: image.data,
    x: container.x,
    y: container.y + (container.h - h) / 2,
    w,
    h,
  });
};

export const handleDownloadEditablePPT = async (
  recceData,
  selectedLayoutType,
  setIsLoading,
  setProgress,
  setDownloadInfo,
  withGeoTag,
) => {
  console.log({ withGeoTag });
  setIsLoading(true);
  imageCache.clear();
  failedImageUrls = new Set();
  setProgress(0);
  setDownloadInfo(null);

  const setSafeProgress = (percent) => {
    setProgress((prev) => Math.max(prev, percent));
  };

  const setPhaseProgress = (start, end, current, total) => {
    if (!total) return;

    const percent = Math.floor(start + (current / total) * (end - start));

    setSafeProgress(percent);
  };

  try {
    const pptx = new PptxGenJS();

    await preloadImages(recceData, selectedLayoutType, ({ current, total }) => {
      setPhaseProgress(0, 45, current, total);
    });

    const pixellLogo = await loadImage("/assests/images/pixell dark.png", true);

    // Fixed: was `selectedLayoutType === "left-right" || "grid-3" || "grid-4"`
    // which was ALWAYS true regardless of selectedLayoutType.
    const isWideLogoLayout = ["left-right", "grid-3", "grid-4"].includes(
      selectedLayoutType,
    );

    for (const [index, recce] of recceData.entries()) {
      const slide = pptx.addSlide();

      // YIELD CONTROL TO BROWSER
      if (index % 3 === 0 && index > 0) {
        await yieldToMain();
      }

      const job = recce.job || {};
      const installationJob = recce?.installationJob;

      let product = {};

      const baseProduct =
        (recce.product &&
          Object.keys(recce.product).length > 0 &&
          recce.product) ||
        recce.job?.product ||
        {};

      const rfpProducts = recce.campaignId?.rfpId?.products || [];

      let rfpMatch = null;

      if (baseProduct.productType && rfpProducts.length > 0) {
        rfpMatch = rfpProducts.find(
          (p) => p.productType === baseProduct.productType,
        );
      }

      product = {
        ...rfpMatch,
        ...baseProduct,
      };

      if (Object.keys(product).length === 0) {
        product = {};
      }

      if (pixellLogo) {
        slide.addImage({
          data: pixellLogo.data,
          x: 8.8,
          y: 0.2,
          w: isWideLogoLayout ? 0.99 : 0.9,
          h: isWideLogoLayout ? 0.4 : 0.4,
        });
      }

      const creativeStatus = recce?.job?.clientCreativeStatus;

      const status =
        creativeStatus === 1
          ? "APPROVED"
          : creativeStatus === -1
            ? "REJECTED"
            : "PENDING";

      const statusColor =
        creativeStatus === 1
          ? "22C55E"
          : creativeStatus === -1
            ? "EF4444"
            : "F59E0B";

      if (selectedLayoutType === "creative-ppt") {
        slide.addShape(pptx.ShapeType.roundRect, {
          x: 7.3,
          y: 0.2,
          w: 1.2,
          h: 0.35,
          fill: { color: statusColor },
          line: { color: statusColor },
          rectRadius: 0.5,
        });

        slide.addText(status, {
          x: 7.3,
          y: 0.2,
          w: 1.2,
          h: 0.35,
          align: "center",
          valign: "middle",
          color: "FFFFFF",
          bold: true,
          fontSize: 12,
        });
      }

      switch (selectedLayoutType) {
        case "recce-ppt": {
          slide.background = { fill: "F3F4F6" };

          const approvedWidth =
            product?.approvedWidth > 0 ? product.approvedWidth : null;
          const approvedHeight =
            product?.approvedHeight > 0 ? product.approvedHeight : null;
          const availableWidth =
            job?.productDimensionsAfterRecce?.width > 0
              ? job.productDimensionsAfterRecce.width
              : null;
          const availableHeight =
            job?.productDimensionsAfterRecce?.height > 0
              ? job.productDimensionsAfterRecce.height
              : null;

          const widthValue = approvedWidth ?? availableWidth ?? "";
          const heightValue = approvedHeight ?? availableHeight ?? "";

          // =========================
          // LEFT CARD SETUP
          // =========================
          const cardX = 0.2;
          const cardY = 0.7;
          const cardW = 6.4;
          const cardH = 4.5;

          slide.addShape(pptx.ShapeType.roundRect, {
            x: cardX,
            y: cardY,
            w: cardW,
            h: cardH,
            fill: { color: "FFFFFF" },
            line: { color: "E5E7EB" },
            rectRadius: 0.1,
          });

          // =========================
          // PRODUCT NAME
          // =========================
          slide.addText(
            `Product Name: ${product.productType || "Not Available"}`,
            {
              x: cardX + 0.1,
              y: cardY + 0.15,
              w: cardW - 0.6,
              h: 0.3,
              fontSize: 14,
              bold: true,
              color: "111827",
            },
          );

          // =========================
          // IMAGE (PERFECT FIT)
          // =========================
          const imageX = cardX + 0.1;
          const imageY = cardY + 0.15;
          const imageW = cardW - 0.2;
          const imageH = cardH + 0.15;

          const imageTasks = [];

          if (job.imageOfRecceSpace) {
            const padding = 0.05;
            imageTasks.push(
              renderImageContain(slide, job.imageOfRecceSpace, {
                x: imageX + padding,
                y: imageY,
                w: imageW - padding * 2,
                h: imageH - padding * 2,
              }),
            );
          }

          await Promise.all(imageTasks);

          if (!job.imageOfRecceSpace) {
            slide.addText("Recce Image Not Available", {
              x: imageX,
              y: imageY + imageH / 2 - 0.3,
              w: imageW,
              h: 0.6,
              align: "center",
              fontSize: 16,
              color: "9CA3AF",
            });
          }

          // =========================
          // RIGHT SIDE CARDS
          // =========================
          const panelX = 6.8;
          const panelW = 3.0;
          let detailY = 0.7;

          const addPanelText = (text, y, bold = false) =>
            addSafeText(slide, panelX, panelW, text, y, bold);

          const createCard = (title, height) => {
            const startY = detailY;

            slide.addShape(pptx.ShapeType.roundRect, {
              x: panelX,
              y: startY,
              w: panelW,
              h: height,
              fill: { color: "FFFFFF" },
              line: { color: "E5E7EB" },
              rectRadius: 0.1,
            });

            slide.addText(title, {
              x: panelX + 0.08,
              y: startY + 0.1,
              w: panelW - 0.2,
              h: 0.3,
              fontSize: 13,
              bold: true,
              color: "111827",
            });

            detailY += height + 0.25;

            return startY;
          };

          // =========================
          // STORE DETAILS
          // =========================
          const storeCardHeight = 1.7;
          const storeStartY = createCard("Store Details", storeCardHeight);

          let textY = storeStartY + 0.35;
          const gap = 0.25;

          addPanelText(`Store: ${job.storeName || "-"}`, textY);
          textY += gap;

          addPanelText(`Owner: ${job.storeOwnerName || "-"}`, textY);
          textY += gap;

          addPanelText(`Phone: ${job.storeOwnerPhoneNumber || "-"}`, textY);
          textY += gap;

          let h = addPanelText(`Address: ${job.address || "-"}`, textY);
          textY += h + 0.05;

          // =========================
          // MEASUREMENT DETAILS
          // =========================
          const width =
            job?.productDimensionsAfterRecce?.width > 0
              ? job.productDimensionsAfterRecce.width
              : null;

          const height =
            job?.productDimensionsAfterRecce?.height > 0
              ? job.productDimensionsAfterRecce.height
              : null;

          const measureCardHeight = 1.4;
          const measureStartY = createCard(
            "Measurement Details",
            measureCardHeight,
          );

          let measureY = measureStartY + 0.35;

          addPanelText(
            `Dimensions (W x H): ${
              width ? Math.round(width * 12) : "-"
            }" x ${height ? Math.round(height * 12) : "-"}`,
            measureY,
          );

          measureY += gap;

          addPanelText(
            width && height
              ? `Area: ${Math.ceil(width * height).toFixed(2)} sq.ft.`
              : `Area: N/A`,
            measureY,
          );

          measureY += gap;

          addPanelText(
            approvedWidth && approvedHeight
              ? `Approved Dimensions: ${Math.round(approvedWidth * 12)}" x ${Math.round(
                  approvedHeight * 12,
                )}"`
              : `Approved Dimensions: N/A`,
            measureY,
            true,
          );

          measureY += gap;

          addPanelText(
            approvedWidth && approvedHeight
              ? `Approved Area: ${Math.ceil(
                  approvedWidth * approvedHeight,
                ).toFixed(2)} sq.ft.`
              : `Approved Area: N/A`,
            measureY,
            true,
          );

          // =========================
          // ADDITIONAL DETAILS
          // =========================
          const additionalCardHeight = 1.1;
          const addStartY = createCard(
            "Additional Details",
            additionalCardHeight,
          );

          let addY = addStartY + 0.35;

          addPanelText(
            `Mounting Base Type: ${job.typeOfMountingBase || "N/A"}`,
            addY,
          );

          addY += gap;

          addPanelText(
            `External Equipments: ${
              job.externalEquipments?.length > 0
                ? job.externalEquipments
                : "N/A"
            }`,
            addY,
          );

          // =========================
          // WIDTH & HEIGHT INPUT FIELD
          // =========================
          slide.addText("Width (ft)", {
            x: 2.2,
            y: 0.28,
            w: 1.0,
            h: 0.32,
            fontSize: 13,
            color: "374151",
            bold: true,
            valign: "middle",
          });

          slide.addShape(pptx.ShapeType.roundRect, {
            x: 3.25,
            y: 0.27,
            w: 0.7,
            h: 0.32,
            fill: { color: "F9FAFB" },
            line: { color: "D1D5DB" },
            rectRadius: 0.05,
          });

          slide.addText(String(widthValue || ""), {
            x: 3.25,
            y: 0.29,
            w: 0.7,
            h: 0.28,
            align: "center",
            fontSize: 12,
            color: "111827",
            bold: true,
          });

          slide.addText("Height (ft)", {
            x: 4.05,
            y: 0.28,
            w: 1.0,
            h: 0.32,
            fontSize: 13,
            color: "374151",
            bold: true,
            valign: "middle",
          });

          slide.addShape(pptx.ShapeType.roundRect, {
            x: 5.1,
            y: 0.27,
            w: 0.7,
            h: 0.32,
            fill: { color: "F9FAFB" },
            line: { color: "D1D5DB" },
            rectRadius: 0.05,
          });

          slide.addText(String(heightValue || ""), {
            x: 5.1,
            y: 0.29,
            w: 0.7,
            h: 0.28,
            align: "center",
            fontSize: 12,
            color: "111827",
            bold: true,
          });

          // =========================
          const clientStatus = recce?.clientStatus;
          let statusText = "Pending";
          let statusColor = "F59E0B"; // Yellow

          if (clientStatus === 1) {
            statusText = "Approved";
            statusColor = "22C55E"; // Green
          } else if (clientStatus === -1) {
            statusText = "Rejected";
            statusColor = "EF4444"; // Red
          }

          slide.addText("Client Approval:", {
            x: 6.0,
            y: 0.28,
            w: 1.6,
            h: 0.32,
            fontSize: 13,
            color: "374151",
            bold: true,
            valign: "middle",
          });

          slide.addShape(pptx.ShapeType.roundRect, {
            x: 7.5,
            y: 0.27,
            w: 0.95,
            h: 0.32,
            fill: { color: statusColor },
            line: { color: statusColor },
            rectRadius: 0.5,
          });

          slide.addText(statusText, {
            x: 7.5,
            y: 0.29,
            w: 0.95,
            h: 0.28,
            align: "center",
            color: "FFFFFF",
            bold: true,
            fontSize: 12,
          });

          // FOOTER
          // =========================
          slide.addImage({
            path: "/assests/pptGradient.webp",
            x: 0,
            y: 5.45,
            w: 10,
            h: 0.18,
          });
          break;
        }

        case "creative-ppt": {
          slide.background = { fill: "F3F4F6" };

          // =========================
          // RECCE CARD
          // =========================
          const cardX = 0.2;
          const cardY = 0.7;
          const cardW = 6.4;
          const cardH = 2.2;

          slide.addShape(pptx.ShapeType.roundRect, {
            x: cardX,
            y: cardY,
            w: cardW,
            h: cardH,
            fill: { color: "FFFFFF" },
            line: { color: "E5E7EB" },
            rectRadius: 0.1,
          });

          // Product name
          slide.addText(
            `Product Name: ${product.productType || "Not Available"}`,
            {
              x: cardX + 0.15,
              y: cardY + 0.1,
              w: cardW - 0.6,
              h: 0.3,
              fontSize: 14,
              bold: true,
            },
          );

          // ========================
          // IMAGE LAYOUT
          // ========================

          const imageTop = cardY + 0.45;
          const imageH = 1.5;
          const imageW = 2.7;

          const leftX = cardX + 0.25;
          const rightX = leftX + imageW + 0.3;

          const imageTasks = [];

          imageTasks.push(
            renderImageContain(slide, job.imageOfRecceSpace, {
              x: leftX,
              y: imageTop,
              w: imageW,
              h: imageH,
            }),
          );

          slide.addText("Recce Image", {
            x: leftX,
            y: imageTop + imageH + 0.05,
            w: imageW,
            h: 0.3,
            fontSize: 9,
            align: "center",
          });

          imageTasks.push(
            renderImageContain(slide, job.clientSideAdaptationImage, {
              x: rightX,
              y: imageTop,
              w: imageW,
              h: imageH,
            }),
          );

          await Promise.all(imageTasks);

          slide.addText("Reference Creative Image", {
            x: rightX,
            y: imageTop + imageH + 0.05,
            w: imageW,
            h: 0.3,
            fontSize: 9,
            align: "center",
          });

          // =========================
          // CREATIVE CARD (BELOW)
          // =========================

          const creativeY = cardY + cardH + 0.25;
          const creativeH = 2.1;

          slide.addShape(pptx.ShapeType.roundRect, {
            x: cardX,
            y: creativeY,
            w: cardW,
            h: creativeH,
            fill: { color: "FFFFFF" },
            line: { color: "E5E7EB" },
            rectRadius: 0.1,
          });

          // Creative title
          slide.addText("Creative Adaptation", {
            x: cardX + 0.15,
            y: creativeY + 0.25,
            w: cardW,
            h: 0.3,
            fontSize: 14,
            bold: true,
          });

          // Creative Image
          if (job.adaptationImage) {
            imageTasks.length = 0;

            imageTasks.push(
              renderImageContain(slide, job.adaptationImage, {
                x: cardX + 0.25,
                y: creativeY + 0.7,
                w: cardW - 0.3,
                h: 1.1,
              }),
            );

            await Promise.all(imageTasks);
          } else {
            slide.addText("Adaptation Not Available", {
              x: cardX + 2.3,
              y: creativeY + 1,
              w: cardW - 0.3,
              h: 0.3,
              fontSize: 10,
              bold: true,
            });
          }

          // =========================
          // RIGHT SIDE CARDS
          // =========================
          const panelX = 6.8;
          const panelW = 3.0;
          let detailY = 0.7;

          //  SAFE TEXT FUNCTION
          const addSafeText = (text, y, bold = false) => {
            const charsPerLine = 35;
            const lines = Math.ceil(text.length / charsPerLine);

            const lineHeight = 0.18;
            const dynamicHeight = Math.max(0.3, lines * lineHeight);

            slide.addText(text, {
              x: panelX + 0.08,
              y,
              w: panelW - 0.2,
              h: dynamicHeight,
              fontSize: 11,
              color: "374151",
              bold,
              wrap: true,
            });

            return dynamicHeight; // important
          };

          // =========================
          // CARD CREATOR
          // =========================
          const createCard = (title, height) => {
            const startY = detailY;

            slide.addShape(pptx.ShapeType.roundRect, {
              x: panelX,
              y: startY,
              w: panelW,
              h: height,
              fill: { color: "FFFFFF" },
              line: { color: "E5E7EB" },
              rectRadius: 0.1,
            });

            slide.addText(title, {
              x: panelX + 0.08,
              y: startY + 0.1,
              w: panelW - 0.2,
              h: 0.3,
              fontSize: 13,
              bold: true,
              color: "111827",
            });

            detailY += height + 0.25;

            return startY;
          };

          // =========================
          // STORE DETAILS
          // =========================
          const storeCardHeight = 1.7;
          const storeStartY = createCard("Store Details", storeCardHeight);

          let textY = storeStartY + 0.35;
          const gap = 0.25;

          addSafeText(`Store: ${job.storeName || "-"}`, textY);
          textY += gap;

          addSafeText(`Owner: ${job.storeOwnerName || "-"}`, textY);
          textY += gap;

          addSafeText(`Phone: ${job.storeOwnerPhoneNumber || "-"}`, textY);
          textY += gap;

          let h = addSafeText(`Address: ${job.address || "-"}`, textY);
          textY += h + 0.05;

          // =========================
          // MEASUREMENT DETAILS
          // =========================
          const width =
            job?.productDimensionsAfterRecce?.width > 0
              ? job.productDimensionsAfterRecce.width
              : null;

          const height =
            job?.productDimensionsAfterRecce?.height > 0
              ? job.productDimensionsAfterRecce.height
              : null;

          const approvedWidth =
            product?.approvedWidth > 0 ? product.approvedWidth : null;

          const approvedHeight =
            product?.approvedHeight > 0 ? product.approvedHeight : null;

          const measureCardHeight = 1.4;
          const measureStartY = createCard(
            "Measurement Details",
            measureCardHeight,
          );

          let measureY = measureStartY + 0.35;

          addSafeText(
            `Dimensions (W x H): ${
              width ? Math.round(width * 12) : "-"
            }" x ${height ? Math.round(height * 12) : "-"}`,
            measureY,
          );

          measureY += gap;

          addSafeText(
            width && height
              ? `Area: ${Math.ceil(width * height).toFixed(2)} sq.ft.`
              : `Area: N/A`,
            measureY,
          );

          measureY += gap;

          addSafeText(
            approvedWidth && approvedHeight
              ? `Approved Dimensions: ${Math.round(approvedWidth * 12)}" x ${Math.round(
                  approvedHeight * 12,
                )}"`
              : `Approved Dimensions: N/A`,
            measureY,
            true,
          );

          measureY += gap;

          addSafeText(
            approvedWidth && approvedHeight
              ? `Approved Area: ${Math.ceil(
                  approvedWidth * approvedHeight,
                ).toFixed(2)} sq.ft.`
              : `Approved Area: N/A`,
            measureY,
            true,
          );

          // =========================
          // ADDITIONAL DETAILS
          // =========================
          const additionalCardHeight = 1.1;
          const addStartY = createCard(
            "Additional Details",
            additionalCardHeight,
          );

          let addY = addStartY + 0.35;

          addSafeText(
            `Mounting Base Type: ${job.typeOfMountingBase || "N/A"}`,
            addY,
          );

          addY += gap;

          addSafeText(
            `External Equipments: ${
              job.externalEquipments?.length > 0
                ? job.externalEquipments
                : "N/A"
            }`,
            addY,
          );

          // =========================
          // FOOTER
          // =========================
          slide.addImage({
            path: "/assests/pptGradient.webp",
            x: 0,
            y: 5.45,
            w: 10,
            h: 0.18,
          });

          break;
        }

        case "completion-ppt": {
          slide.background = { fill: "F3F4F6" };

          // =========================
          // RECCE CARD
          // =========================
          const cardX = 0.2;
          const cardY = 0.7;
          const cardW = 6.4;
          const cardH = 2.2;

          slide.addShape(pptx.ShapeType.roundRect, {
            x: cardX,
            y: cardY,
            w: cardW,
            h: cardH,
            fill: { color: "FFFFFF" },
            line: { color: "E5E7EB" },
            rectRadius: 0.1,
          });

          slide.addText(
            `Product Name: ${product.productType || "Not Available"}`,
            {
              x: cardX + 0.15,
              y: cardY + 0.1,
              w: cardW - 0.6,
              h: 0.3,
              fontSize: 14,
              bold: true,
            },
          );

          // ========================
          // IMAGE LAYOUT
          // ========================
          const imageTop = cardY + 0.45;
          const imageH = 1.5;
          const imageW = 2.7;

          const leftX = cardX + 0.25;
          const rightX = leftX + imageW + 0.3;

          const imageTasks = [];

          imageTasks.push(
            renderImageContain(slide, job.imageOfRecceSpace, {
              x: leftX,
              y: imageTop,
              w: imageW,
              h: imageH,
            }),
          );

          imageTasks.push(
            renderImageContain(
              slide,
              withGeoTag?.geoTag
                ? installationJob?.imagesOfInstallation?.frontPhoto
                : installationJob?.imagesOfInstallation?.leftPhoto ||
                    recce?.installationJob?.imagesOfInstallation?.frontPhoto,
              {
                x: rightX,
                y: imageTop,
                w: imageW,
                h: imageH,
              },
            ),
          );

          await Promise.all(imageTasks);

          slide.addText("Recce Image", {
            x: leftX,
            y: imageTop + imageH + 0.05,
            w: imageW,
            h: 0.3,
            fontSize: 9,
            align: "center",
          });

          slide.addText("Installation Image", {
            //  FIXED LABEL
            x: rightX,
            y: imageTop + imageH + 0.05,
            w: imageW,
            h: 0.3,
            fontSize: 9,
            align: "center",
          });

          // =========================
          // CREATIVE CARD (BELOW)
          // =========================
          const creativeY = cardY + cardH + 0.25;
          const creativeH = 2.1;

          slide.addShape(pptx.ShapeType.roundRect, {
            x: cardX,
            y: creativeY,
            w: cardW,
            h: creativeH,
            fill: { color: "FFFFFF" },
            line: { color: "E5E7EB" },
            rectRadius: 0.1,
          });

          slide.addText("Creative Adaptation", {
            x: cardX + 0.15,
            y: creativeY + 0.25,
            w: cardW,
            h: 0.3,
            fontSize: 14,
            bold: true,
          });

          if (job.adaptationImage) {
            const adaptationTasks = [];
            adaptationTasks.push(
              renderImageContain(slide, job.adaptationImage, {
                x: cardX + 0.25,
                y: creativeY + 0.7,
                w: cardW - 0.3,
                h: 1.1,
              }),
            );
            await Promise.all(adaptationTasks);
          } else {
            slide.addText("Adaptation Not Available", {
              x: cardX + 2.3,
              y: creativeY + 1,
              w: cardW - 0.3,
              h: 0.3,
              fontSize: 10,
              bold: true,
            });
          }

          // =========================
          // RIGHT SIDE CARDS
          // =========================
          const panelX = 6.8;
          const panelW = 3.0;
          let detailY = 0.7;

          const addSafeText = (text, y, bold = false) => {
            const charsPerLine = 35;
            const lines = Math.ceil(text.length / charsPerLine);

            const lineHeight = 0.18;
            const dynamicHeight = Math.max(0.3, lines * lineHeight);

            slide.addText(text, {
              x: panelX + 0.08,
              y,
              w: panelW - 0.2,
              h: dynamicHeight,
              fontSize: 11,
              color: "374151",
              bold,
              wrap: true,
            });

            return dynamicHeight;
          };

          const createCard = (title, height) => {
            const startY = detailY;

            slide.addShape(pptx.ShapeType.roundRect, {
              x: panelX,
              y: startY,
              w: panelW,
              h: height,
              fill: { color: "FFFFFF" },
              line: { color: "E5E7EB" },
              rectRadius: 0.1,
            });

            slide.addText(title, {
              x: panelX + 0.08,
              y: startY + 0.1,
              w: panelW - 0.2,
              h: 0.3,
              fontSize: 13,
              bold: true,
              color: "111827",
            });

            detailY += height + 0.25;

            return startY;
          };

          // =========================
          // STORE DETAILS
          // =========================
          const storeCardHeight = 1.7;
          const storeStartY = createCard("Store Details", storeCardHeight);

          let textY = storeStartY + 0.35;
          const gap = 0.25;

          addSafeText(`Store: ${job.storeName || "-"}`, textY);
          textY += gap;

          addSafeText(`Owner: ${job.storeOwnerName || "-"}`, textY);
          textY += gap;

          addSafeText(`Phone: ${job.storeOwnerPhoneNumber || "-"}`, textY);
          textY += gap;

          let h = addSafeText(`Address: ${job.address || "-"}`, textY);
          textY += h + 0.05;

          // =========================
          // MEASUREMENT DETAILS
          // =========================
          const width =
            job?.productDimensionsAfterRecce?.width > 0
              ? job.productDimensionsAfterRecce.width
              : null;

          const height =
            job?.productDimensionsAfterRecce?.height > 0
              ? job.productDimensionsAfterRecce.height
              : null;

          const approvedWidth =
            product?.approvedWidth > 0 ? product.approvedWidth : null;

          const approvedHeight =
            product?.approvedHeight > 0 ? product.approvedHeight : null;

          const measureCardHeight = 1.4;
          const measureStartY = createCard(
            "Measurement Details",
            measureCardHeight,
          );

          let measureY = measureStartY + 0.35;

          addSafeText(
            `Dimensions (W x H): ${
              width ? Math.round(width * 12) : "-"
            }" x ${height ? Math.round(height * 12) : "-"}`,
            measureY,
          );

          measureY += gap;

          addSafeText(
            width && height
              ? `Area: ${Math.ceil(width * height).toFixed(2)} sq.ft.`
              : `Area: N/A`,
            measureY,
          );

          measureY += gap;

          addSafeText(
            approvedWidth && approvedHeight
              ? `Approved Dimensions: ${Math.round(approvedWidth * 12)}" x ${Math.round(
                  approvedHeight * 12,
                )}"`
              : `Approved Dimensions: N/A`,
            measureY,
            true,
          );

          measureY += gap;

          addSafeText(
            approvedWidth && approvedHeight
              ? `Approved Area: ${Math.ceil(
                  approvedWidth * approvedHeight,
                ).toFixed(2)} sq.ft.`
              : `Approved Area: N/A`,
            measureY,
            true,
          );

          // =========================
          // ADDITIONAL DETAILS
          // =========================
          const additionalCardHeight = 1.1;
          const addStartY = createCard(
            "Additional Details",
            additionalCardHeight,
          );

          let addY = addStartY + 0.35;

          addSafeText(
            `Mounting Base Type: ${job.typeOfMountingBase || "N/A"}`,
            addY,
          );

          addY += gap;

          addSafeText(
            `External Equipments: ${
              job.externalEquipments?.length > 0
                ? job.externalEquipments
                : "N/A"
            }`,
            addY,
          );

          // =========================
          // FOOTER
          // =========================
          slide.addImage({
            path: "/assests/pptGradient.webp",
            x: 0,
            y: 5.45,
            w: 10,
            h: 0.18,
          });

          break;
        }

        default:
          break;
      }

      if (index % 2 === 0 || index === recceData.length - 1) {
        setPhaseProgress(45, 90, index + 1, recceData.length);
        await yieldToMain();
      }
    }

    // Fixed: was `recceData[0].campaignId.campaignTitle` with no guard â€”
    // threw a TypeError (and silently hung the UI, see catch block below)
    // whenever campaignId wasn't populated.
    const campaignTitle = recceData?.[0]?.campaignId?.campaignTitle || "Recce";

    const fileNameMap = {
      "recce-ppt": `${campaignTitle}_Recce_PPT.pptx`,
      "creative-ppt": `${campaignTitle}_Creative_PPT.pptx`,
      "completion-ppt": `${campaignTitle}_Completion_PPT.pptx`,
    };

    setSafeProgress(92);
    await yieldToMain();

    const blob = await pptx.write({ outputType: "blob" });

    setSafeProgress(98);
    await yieldToMain();

    const url = URL.createObjectURL(blob);
    setDownloadInfo({
      url,
      fileName: fileNameMap[selectedLayoutType] || "Recce_PPT.pptx",
    });

    setSafeProgress(100);

    if (failedImageUrls.size > 0) {
      console.warn(
        `[PPT export] ${failedImageUrls.size} image(s) could not be loaded and were skipped:`,
        [...failedImageUrls],
      );
      Swal.fire({
        icon: "warning",
        title: "Some images were skipped",
        text: `${failedImageUrls.size} image${
          failedImageUrls.size > 1 ? "s" : ""
        } could not be loaded and ${
          failedImageUrls.size > 1 ? "were" : "was"
        } left out of the PPT. Check your internet connection or the image links, then try again. (See browser console for the full list.)`,
        customClass: {
          container: "my-swal",
        },
      });
    }
  } catch (error) {
    console.error("PPT export failed:", error);
    Swal.fire({
      icon: "error",
      title: "Download Failed",
      text: error?.message
        ? `Something went wrong while generating the PPT: ${error.message}`
        : "Something went wrong while generating the PPT. Please try again.",
      customClass: {
        container: "my-swal",
      },
    });
  } finally {
    setIsLoading(false);
  }
};
